import { type AssociationRecord } from "../association";

export enum RepresentationStorageFormat {
  MCAP = "mcap",
  PARQUET = "parquet",
}

export interface RepresentationRecord {
  association: AssociationRecord;
  context: Record<string, string>;
  /**
   * Content format descriptor for this representation.
   * For image topics: the image encoding (e.g. "jpeg", "png") for processed representations,
   * or the ROS schema name (e.g. "sensor_msgs/Image") for original representations.
   * Undefined for non-image topics or legacy representations.
   */
  format?: string;
  representation_id: string;
  storage_format: RepresentationStorageFormat;
  /**
   * Ordered list of transformation descriptors applied to produce this representation.
   * Empty or undefined for original/passthrough representations.
   * Example: ["downsample:0.5", "encode:jpeg"]
   */
  transformations?: string[];
}

const SUPPORTED_IMAGE_FORMATS = new Set(["jpeg", "jpg", "png"]);

/**
 * Select the best representation for a given set of supported formats.
 *
 * When `supportedFormats` is non-empty (e.g. image panels requesting ["jpeg", "png"]):
 * 1. A representation whose `format` matches one of `supportedFormats`.
 * 2. A processed representation (non-empty `transformations`), since processed
 *    representations are produced specifically for visualisation (e.g. JPEG-encoded images).
 * 3. The first available representation as a last resort.
 *
 * When `supportedFormats` is empty (e.g. data panels like Log, Plot, Map):
 * 1. An original representation (no transformations), since data panels
 *    need the complete original message structure.
 * 2. The first available representation as a last resort.
 */
export function selectRepresentationForFormats(
  representations: RepresentationRecord[],
  supportedFormats: string[],
): RepresentationRecord | null {
  if (representations.length === 0) {
    return null;
  }

  if (supportedFormats.length > 0) {
    // 1. Prefer a representation whose format matches a supported format.
    const supported = new Set(supportedFormats.map((f) => f.toLowerCase()));
    const match = representations.find(
      (r) => r.format !== undefined && supported.has(r.format.toLowerCase()),
    );
    if (match) {
      return match;
    }

    // 2. Prefer a processed representation (has transformations).
    const processed = representations.find(
      (r) => r.transformations && r.transformations.length > 0,
    );
    if (processed) {
      return processed;
    }
  } else {
    // Prefer an original representation (no transformations).
    const original = representations.find(
      (r) => !r.transformations || r.transformations.length === 0,
    );
    if (original) {
      return original;
    }
  }

  // Fall back to the first available representation.
  return representations[0];
}

export { SUPPORTED_IMAGE_FORMATS };
